// SCR-STAFF-856 (AP-INS-02؛ US-INS-090 تا 092): سنی مطالبات باز، تحلیل کسورات (نرخ، سهم علت‌ها، ۱۰ خدمت و ارائه‌دهنده برتر،
// روند ماهانه و نرخ موفقیت اعتراض) و عملکرد بیمه‌گر (حجم مطالبه، نرخ کسورات، دوره وصول و تعداد بیمه‌شدگان). همه نرخ‌ها در سرور
// در ده‌هزار محاسبه می‌شوند و این صفحه فقط آن‌ها را درصد می‌کند (ADR-013).
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type InsAppealSuccessRowView,
  type InsClaimAgingRowView,
  type InsDeductionRankRowView,
  type InsDeductionReasonShareView,
  type InsDeductionTrendRowView,
  type InsInsurerPerformanceRowView,
} from '@phoenix/api-client';
import { useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, ErrorState, faDigits, KeyValueList, ListPage, Money, type DataTableColumn } from '@phoenix/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { claimKeys, ratePercent } from './claims';

export const SCR_STAFF_856 = 'SCR-STAFF-856';

export function ClaimReportsPage() {
  const { t } = useTranslation('ins');
  const [tab, setTab] = useState<'aging' | 'deductions' | 'performance'>('aging');

  return (
    <ListPage
      screenId={SCR_STAFF_856}
      titleKey="ins:claim_reports.title"
      subtitleKey="ins:claim_reports.subtitle"
      filters={
        <Tabs value={tab} onChange={(_, value: 'aging' | 'deductions' | 'performance') => setTab(value)}>
          <Tab value="aging" label={t('ins:claim_reports.tab_aging')} data-testid="ins-tab-aging" />
          <Tab value="deductions" label={t('ins:claim_reports.tab_deductions')} data-testid="ins-tab-analytics" />
          <Tab value="performance" label={t('ins:claim_reports.tab_performance')} data-testid="ins-tab-performance" />
        </Tabs>
      }
    >
      {tab === 'aging' ? <AgingTab /> : tab === 'deductions' ? <AnalyticsTab /> : <PerformanceTab />}
    </ListPage>
  );
}

/** سنی مطالبات باز به تفکیک بیمه‌گر، شعبه و دسته سنی از تاریخ ارسال (AC-INS-090.1). */
function AgingTab() {
  const { tenantApi, session } = useTenantSession();
  const aging = useQuery({
    queryKey: claimKeys.aging(session?.tenantId, 'all'),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/insurance/claim-reports/aging', { params: { query: {} } })),
  });

  const columns: DataTableColumn<InsClaimAgingRowView>[] = [
    { id: 'insurer', headerKey: 'ins:claim_reports.columns.insurer', cell: (row) => row.insurerName },
    {
      id: 'bucket',
      headerKey: 'ins:claim_reports.columns.bucket',
      cell: (row) => <BucketLabel bucket={row.bucket} />,
    },
    { id: 'items', headerKey: 'ins:claim_reports.columns.items', cell: (row) => faDigits(Number(row.itemCount)), align: 'right' },
    { id: 'open', headerKey: 'ins:claim_reports.columns.open', cell: (row) => <Money amountMinor={Number(row.open.amount)} />, align: 'right' },
  ];

  return (
    <DataTable
      captionKey="ins:claim_reports.aging_caption"
      columns={columns}
      rows={aging.data}
      getRowId={(row) => `${row.insurerId}-${row.branchId}-${row.bucket}`}
      isLoading={aging.isPending}
      error={aging.error}
      onRetry={() => void aging.refetch()}
      emptyTitleKey="ins:claim_reports.aging_empty"
    />
  );
}

function BucketLabel({ bucket }: { bucket: InsClaimAgingRowView['bucket'] }) {
  const { t } = useTranslation('ins');
  return <span data-testid={`ins-aging-${bucket}`}>{t(`ins:age_bucket.${bucket}`)}</span>;
}

/** تحلیل کسورات: نرخ، سهم هر علت، خدمات و ارائه‌دهندگان برتر، روند ماهانه و نرخ موفقیت اعتراض (AC-INS-091.1). */
function AnalyticsTab() {
  const { t } = useTranslation('ins');
  const { tenantApi, session } = useTenantSession();
  const analytics = useQuery({
    queryKey: claimKeys.analytics(session?.tenantId, 'all'),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/insurance/claim-reports/deductions', { params: { query: {} } })),
  });

  if (analytics.error) {
    return <ErrorState error={analytics.error} onRetry={() => void analytics.refetch()} />;
  }

  const data = analytics.data;

  const reasonColumns: DataTableColumn<InsDeductionReasonShareView>[] = [
    { id: 'reason', headerKey: 'ins:claim_reports.columns.reason', cell: (row) => t(`ins:deduction_reason.${row.reasonCode}`) },
    { id: 'amount', headerKey: 'ins:claim_reports.columns.amount', cell: (row) => <Money amountMinor={Number(row.amount.amount)} />, align: 'right' },
    { id: 'share', headerKey: 'ins:claim_reports.columns.share', cell: (row) => t('ins:claim_reports.percent', { value: ratePercent(row.shareBasisPoints) }), align: 'right' },
  ];

  const rankColumns: DataTableColumn<InsDeductionRankRowView>[] = [
    { id: 'name', headerKey: 'ins:claim_reports.columns.name', cell: (row) => row.name },
    { id: 'claimed', headerKey: 'ins:claim_reports.columns.claimed', cell: (row) => <Money amountMinor={Number(row.claimed.amount)} />, align: 'right' },
    { id: 'deducted', headerKey: 'ins:claim_reports.columns.deducted', cell: (row) => <Money amountMinor={Number(row.deducted.amount)} />, align: 'right' },
    { id: 'rate', headerKey: 'ins:claim_reports.columns.rate', cell: (row) => t('ins:claim_reports.percent', { value: ratePercent(row.rateBasisPoints) }), align: 'right' },
  ];

  const trendColumns: DataTableColumn<InsDeductionTrendRowView>[] = [
    { id: 'period', headerKey: 'ins:claim_reports.columns.period', cell: (row) => row.periodLabel },
    { id: 'claimed', headerKey: 'ins:claim_reports.columns.claimed', cell: (row) => <Money amountMinor={Number(row.claimed.amount)} />, align: 'right' },
    { id: 'deducted', headerKey: 'ins:claim_reports.columns.deducted', cell: (row) => <Money amountMinor={Number(row.deducted.amount)} />, align: 'right' },
    { id: 'rate', headerKey: 'ins:claim_reports.columns.rate', cell: (row) => t('ins:claim_reports.percent', { value: ratePercent(row.rateBasisPoints) }), align: 'right' },
  ];

  const appealColumns: DataTableColumn<InsAppealSuccessRowView>[] = [
    { id: 'reason', headerKey: 'ins:claim_reports.columns.reason', cell: (row) => t(`ins:deduction_reason.${row.reasonCode}`) },
    { id: 'disputed', headerKey: 'ins:claim_reports.columns.disputed', cell: (row) => <Money amountMinor={Number(row.disputed.amount)} />, align: 'right' },
    { id: 'recovered', headerKey: 'ins:claim_reports.columns.recovered', cell: (row) => <Money amountMinor={Number(row.recovered.amount)} />, align: 'right' },
    { id: 'success', headerKey: 'ins:claim_reports.columns.success', cell: (row) => t('ins:claim_reports.percent', { value: ratePercent(row.successBasisPoints) }), align: 'right' },
  ];

  return (
    <Stack spacing={3}>
      <KeyValueList
        columns={3}
        items={[
          { labelKey: 'ins:claim_reports.claimed', value: <Money amountMinor={Number(data?.claimed.amount ?? 0)} /> },
          { labelKey: 'ins:claim_reports.deducted', value: <Money amountMinor={Number(data?.deducted.amount ?? 0)} /> },
          {
            labelKey: 'ins:claim_reports.rate',
            value: (
              <Typography component="span" data-testid="ins-deduction-rate">
                {t('ins:claim_reports.percent', { value: ratePercent(data?.rateBasisPoints) })}
              </Typography>
            ),
          },
        ]}
      />
      <DataTable
        captionKey="ins:claim_reports.reasons_caption"
        columns={reasonColumns}
        rows={data?.reasons}
        getRowId={(row) => String(row.reasonCode)}
        isLoading={analytics.isPending}
        emptyTitleKey="ins:claim_reports.empty"
      />
      <DataTable
        captionKey="ins:claim_reports.top_services_caption"
        columns={rankColumns}
        rows={data?.topServices}
        getRowId={(row) => row.id}
        isLoading={analytics.isPending}
        emptyTitleKey="ins:claim_reports.empty"
      />
      <DataTable
        captionKey="ins:claim_reports.top_providers_caption"
        columns={rankColumns}
        rows={data?.topProviders}
        getRowId={(row) => row.id}
        isLoading={analytics.isPending}
        emptyTitleKey="ins:claim_reports.empty"
      />
      <DataTable
        captionKey="ins:claim_reports.trend_caption"
        columns={trendColumns}
        rows={data?.trend}
        getRowId={(row) => row.periodLabel}
        isLoading={analytics.isPending}
        emptyTitleKey="ins:claim_reports.empty"
      />
      <DataTable
        captionKey="ins:claim_reports.appeals_caption"
        columns={appealColumns}
        rows={data?.appeals}
        getRowId={(row) => String(row.reasonCode)}
        isLoading={analytics.isPending}
        emptyTitleKey="ins:claim_reports.empty"
      />
    </Stack>
  );
}

/** عملکرد بیمه‌گر (AC-INS-092.1): حجم، نرخ کسورات، دوره وصول، سهم از درآمد و تعداد بیمه‌شدگان. */
function PerformanceTab() {
  const { t } = useTranslation('ins');
  const { tenantApi, session } = useTenantSession();
  const performance = useQuery({
    queryKey: claimKeys.performance(session?.tenantId, 'all'),
    queryFn: () =>
      unwrap(tenantApi.GET('/api/v1/insurance/claim-reports/insurer-performance', { params: { query: {} } })),
  });

  const columns: DataTableColumn<InsInsurerPerformanceRowView>[] = [
    { id: 'insurer', headerKey: 'ins:claim_reports.columns.insurer', cell: (row) => row.insurerName },
    { id: 'claimed', headerKey: 'ins:claim_reports.columns.claimed', cell: (row) => <Money amountMinor={Number(row.claimed.amount)} />, align: 'right' },
    { id: 'deducted', headerKey: 'ins:claim_reports.columns.deducted', cell: (row) => <Money amountMinor={Number(row.deducted.amount)} />, align: 'right' },
    { id: 'paid', headerKey: 'ins:claim_reports.columns.paid', cell: (row) => <Money amountMinor={Number(row.paid.amount)} />, align: 'right' },
    { id: 'open', headerKey: 'ins:claim_reports.columns.open', cell: (row) => <Money amountMinor={Number(row.open.amount)} />, align: 'right' },
    {
      id: 'rate',
      headerKey: 'ins:claim_reports.columns.rate',
      cell: (row) => (
        <span data-testid={`ins-performance-rate-${row.insurerId}`}>{t('ins:claim_reports.percent', { value: ratePercent(row.deductionRateBasisPoints) })}</span>
      ),
      align: 'right',
    },
    {
      id: 'collection_days',
      headerKey: 'ins:claim_reports.columns.collection_days',
      cell: (row) => (row.collectionDays === null ? '—' : faDigits(Number(row.collectionDays))),
      align: 'right',
    },
    {
      id: 'revenue_share',
      headerKey: 'ins:claim_reports.columns.revenue_share',
      cell: (row) => t('ins:claim_reports.percent', { value: ratePercent(row.shareOfRevenueBasisPoints) }),
      align: 'right',
    },
    { id: 'patients', headerKey: 'ins:claim_reports.columns.patients', cell: (row) => faDigits(Number(row.insuredPatientCount)), align: 'right' },
  ];

  return (
    <DataTable
      captionKey="ins:claim_reports.performance_caption"
      columns={columns}
      rows={performance.data}
      getRowId={(row) => row.insurerId}
      isLoading={performance.isPending}
      error={performance.error}
      onRetry={() => void performance.refetch()}
      emptyTitleKey="ins:claim_reports.performance_empty"
    />
  );
}
